/**
 * REUSABLE UTILITY MODULE - Formatters
 * Can be used in any React project
 */

/**
 * Format date to locale string
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale (default: 'fr-FR')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date
 */
export const formatDate = (date, locale = 'fr-FR', options = {}) => {
  if (!date) return '-';

  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options,
  };

  try {
    return new Date(date).toLocaleDateString(locale, defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

/**
 * Format date and time
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale (default: 'fr-FR')
 * @returns {string} Formatted date and time
 */
export const formatDateTime = (date, locale = 'fr-FR') => {
  if (!date) return '-';

  try {
    return new Date(date).toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return '-';
  }
};

/**
 * Format relative time (e.g., "il y a 2 jours")
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale (default: 'fr-FR')
 * @returns {string} Relative time
 */
export const formatRelativeTime = (date, locale = 'fr-FR') => {
  if (!date) return '-';

  try {
    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now - past) / 1000);

    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

    if (diffInSeconds < 60) {
      return rtf.format(-diffInSeconds, 'second');
    } else if (diffInSeconds < 3600) {
      return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
    } else if (diffInSeconds < 86400) {
      return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
    } else if (diffInSeconds < 604800) {
      return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
    } else if (diffInSeconds < 2592000) {
      return rtf.format(-Math.floor(diffInSeconds / 604800), 'week');
    } else if (diffInSeconds < 31536000) {
      return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
    } else {
      return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return '-';
  }
};

/**
 * Format currency
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'EUR')
 * @param {string} locale - Locale (default: 'fr-FR')
 * @returns {string} Formatted currency
 */
export const formatCurrency = (amount, currency = 'EUR', locale = 'fr-FR') => {
  if (amount === null || amount === undefined) return '-';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return '-';
  }
};

/**
 * Format number with separators
 * @param {number} number - Number to format
 * @param {string} locale - Locale (default: 'fr-FR')
 * @param {number} decimals - Number of decimals (default: 0)
 * @returns {string} Formatted number
 */
export const formatNumber = (number, locale = 'fr-FR', decimals = 0) => {
  if (number === null || number === undefined) return '-';

  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(number);
  } catch (error) {
    console.error('Error formatting number:', error);
    return '-';
  }
};

/**
 * Format percentage
 * @param {number} value - Value to format (0-100 or 0-1)
 * @param {boolean} isDecimal - If true, value is between 0-1 (default: false)
 * @param {number} decimals - Number of decimals (default: 1)
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, isDecimal = false, decimals = 1) => {
  if (value === null || value === undefined) return '-';

  const percentage = isDecimal ? value * 100 : value;
  return `${formatNumber(percentage, 'fr-FR', decimals)}%`;
};

/**
 * Format file size
 * @param {number} bytes - Size in bytes
 * @param {number} decimals - Number of decimals (default: 2)
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  if (!bytes) return '-';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

/**
 * Format phone number (French format)
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 */
export const formatPhone = (phone) => {
  if (!phone) return '-';

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Format French phone number: +33 6 12 34 56 78
  if (cleaned.startsWith('33')) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9, 11)}`;
  }

  // Format local number: 06 12 34 56 78
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
  }

  return phone;
};

/**
 * Truncate text
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length (default: 50)
 * @param {string} suffix - Suffix to add (default: '...')
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50, suffix = '...') => {
  if (!text) return '';
  if (text.length <= maxLength) return text;

  return text.slice(0, maxLength - suffix.length) + suffix;
};

/**
 * Capitalize first letter
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalizeFirst = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Convert snake_case to Title Case
 * @param {string} text - Text to convert
 * @returns {string} Title case text
 */
export const snakeToTitle = (text) => {
  if (!text) return '';
  return text
    .split('_')
    .map(word => capitalizeFirst(word))
    .join(' ');
};

/**
 * Format duration in milliseconds to human readable
 * @param {number} ms - Duration in milliseconds
 * @param {string} locale - Locale (default: 'fr-FR')
 * @returns {string} Human readable duration
 */
export const formatDuration = (ms, locale = 'fr-FR') => {
  if (!ms) return '-';

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}j ${hours % 24}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}min`;
  } else if (minutes > 0) {
    return `${minutes}min ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

/**
 * Get initials from name
 * @param {string} name - Full name
 * @returns {string} Initials (max 2 letters)
 */
export const getInitials = (name) => {
  if (!name) return '';

  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Format credit card number (masked)
 * @param {string} cardNumber - Card number
 * @returns {string} Masked card number (e.g., "**** 4242")
 */
export const formatCardNumber = (cardNumber) => {
  if (!cardNumber) return '-';

  const cleaned = cardNumber.replace(/\D/g, '');
  if (cleaned.length < 4) return cardNumber;

  return `•••• ${cleaned.slice(-4)}`;
};

const formattersExport = {
  formatDate,
  formatDateTime,
  formatRelativeTime,
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatFileSize,
  formatPhone,
  truncateText,
  capitalizeFirst,
  snakeToTitle,
  formatDuration,
  getInitials,
  formatCardNumber,
};

export default formattersExport;
